import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useApi, useRealtimeEvent, type AgentRun, type AgentStep } from "@ncp/api-client";
import { useEnumLabel, useLocale, useTranslation } from "@ncp/i18n";
import { AgentActivityFeed, Badge, Button, Card, CardContent, CardHeader, CardTitle, Skeleton, StatusBadge } from "@ncp/ui";

/** Live view of one agent run: persisted steps plus realtime step events while the run is in progress. */
export function AgentRunPanel({ runId, onClose, compact }: { runId: string; onClose?: () => void; compact?: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const enumLabel = useEnumLabel();
  const api = useApi();
  const run = useQuery({ queryKey: ["agent-run", runId], queryFn: () => api.get<AgentRun>(`/api/ai/runs/${runId}`), refetchInterval: (q) => (q.state.data?.status === "Running" || q.state.data?.status === "Queued" ? 3000 : false) });
  const [liveSteps, setLiveSteps] = useState<AgentStep[]>([]);

  useEffect(() => setLiveSteps([]), [runId]);
  useRealtimeEvent<{ runId: string; step?: AgentStep }>("agent.step", (p) => {
    if (p.runId !== runId || !p.step) return;
    setLiveSteps((prev) => (prev.some((s) => s.id === p.step!.id) ? prev : [...prev, p.step!]));
  });

  const steps = [...(run.data?.steps ?? []), ...liveSteps.filter((s) => !(run.data?.steps ?? []).some((x) => x.id === s.id))].sort((a, b) => a.sequence - b.sequence);
  const running = run.data?.status === "Running" || run.data?.status === "Queued";

  return (
    <Card className={compact ? "" : "max-h-[75vh] flex flex-col"}>
      <CardHeader className="flex-row items-start justify-between gap-2">
        <div>
          <CardTitle className="flex items-center gap-2">{t("ai.agentActivity")}{run.data ? <StatusBadge status={run.data.status === "Succeeded" ? "Approved" : run.data.status === "Failed" ? "Rejected" : "UnderReview"} label={enumLabel("runStatus", run.data.status)} /> : null}</CardTitle>
          {run.data ? (
            <p className="mt-1 text-xs text-muted-foreground">
              {locale === "ar" ? (run.data.agentNameAr ?? run.data.agentName) : run.data.agentName}
              {run.data.piiTokensRedacted > 0 ? <Badge variant="info" size="sm" className="ms-2" title={t("ai.piiRedacted")}>{t("ai.piiMasked", { count: run.data.piiTokensRedacted })}</Badge> : null}
            </p>
          ) : null}
        </div>
        {onClose ? <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label={t("actions.close")}><X /></Button> : null}
      </CardHeader>
      <CardContent className="min-h-0 flex-1 overflow-y-auto scrollbar-thin" tabIndex={0} role="region" aria-label={t("ai.agentActivity")}>
        {run.isLoading ? <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 rounded-xl" />)}</div> : <AgentActivityFeed steps={steps} running={running} compact={compact} />}
        {run.data?.error ? (locale === "ar" && run.data.errorAr
          ? <p className="mt-3 rounded-lg bg-danger-50 p-2 text-xs text-danger-700">{run.data.errorAr}</p>
          : <p className="mt-3 rounded-lg bg-danger-50 p-2 text-xs text-danger-700" dir="auto" lang="en">{run.data.error}</p>) : null}
      </CardContent>
    </Card>
  );
}
